import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { ChipAutocompleteOption } from '../../../core/models/chip-autocomplete-option';
import { apiUrl } from '../../../core/config/api-endpoints';
import {
  SLAEscalationConfigurationResponse,
  SLAEscalationConfigurationSaveRequest,
  SLAEscalationRoleOptionsResponse,
  SLAEscalationStatusRequest,
  SLAEscalationStatusResponse,
  SLAEscalationUserOptionsResponse,
} from '../models/sla-escalation.model';

@Injectable({ providedIn: 'root' })
export class ConfigurationsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = apiUrl('/portal/configurations/sla-escalation-settings');

  getSlaEscalationSettings(): Observable<SLAEscalationConfigurationResponse> {
    return this.http.get<SLAEscalationConfigurationResponse>(this.baseUrl);
  }

  saveSlaEscalationSettings(
    payload: SLAEscalationConfigurationSaveRequest,
  ): Observable<SLAEscalationConfigurationResponse> {
    return this.http.post<SLAEscalationConfigurationResponse>(this.baseUrl, payload);
  }

  updateSlaEscalationStatus(
    payload: SLAEscalationStatusRequest,
  ): Observable<SLAEscalationStatusResponse> {
    return this.http.patch<SLAEscalationStatusResponse>(`${this.baseUrl}/status`, payload);
  }

  /** Active user emails for alert recipients (policy: escalation_rules VIEW). */
  listEscalationUserEmails(search?: string): Observable<string[]> {
    let params = new HttpParams();
    const normalized = search?.trim();
    if (normalized) {
      params = params.set('search', normalized);
    }
    return this.http
      .get<SLAEscalationUserOptionsResponse>(`${this.baseUrl}/users`, { params })
      .pipe(
        map((response) =>
          [
            ...new Set(
              response.items.map((item) => item.email.trim().toLowerCase()).filter(Boolean),
            ),
          ].sort(),
        ),
      );
  }

  /** Active roles for escalate-to (policy: escalation_rules VIEW). */
  listEscalationRoleOptions(search?: string): Observable<ChipAutocompleteOption[]> {
    let params = new HttpParams();
    const normalized = search?.trim();
    if (normalized) {
      params = params.set('search', normalized);
    }
    return this.http
      .get<SLAEscalationRoleOptionsResponse>(`${this.baseUrl}/roles`, { params })
      .pipe(
        map((response) =>
          response.items.map((role) => ({
            value: role.id,
            label: role.name,
          })),
        ),
      );
  }
}
